import React, { FC, ReactNode } from 'react';

import { FormattedMessage, FormattedNumber } from 'react-intl';

import { ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import CommonMessages from '~/core/commonMessages';
import { CountryCode } from '~/core/enums';
import { parseValueFromLocalizedAttribute } from '~/core/utils';
import TeamSituation from '~/views/components/teamSituation/TeamSituation';

import { ScheduleTeam, ScoresGame, TeamOdds } from '../../../../types';

import {
    AdditionalTeamInfo,
    TeamContainer,
    TeamGameDetails,
    TeamInfoContainer,
    TeamLogo,
    TeamName,
    TeamOddsContainer,
    TeamScore,
} from './styles';

type TeamDisplayProps = {
    isHomeTeam: boolean;
    team: ScheduleTeam;
    odds?: TeamOdds;
    preGame: boolean;
    countryCode: CountryCode;
    game: ScoresGame;
    hideOdds: boolean;
    partnerId?: number;
};

const TEAM_ALT_TEXT_OVERRIDE = '';

const getAdditionalTeamInfo = (
    pregame: boolean,
    sog?: number,
    record?: string,
    situationDescriptions?: Array<ShortCodeEnums.SituationCode>,
    situationTimeRemaining?: string
): ReactNode =>
    pregame ? (
        record
    ) : (
        <AdditionalTeamInfo className="additional-team-info">
            {sog !== undefined && (
                <FormattedMessage {...CommonMessages.statsDescriptions.long.sogWithAmount} values={{ value: sog }} />
            )}
            <TeamSituation descriptions={situationDescriptions} timeRemaining={situationTimeRemaining} />
        </AdditionalTeamInfo>
    );

const FormattedTeamDisplay: FC<TeamDisplayProps> = ({
    isHomeTeam,
    team,
    preGame,
    countryCode,
    hideOdds,
    game,
    partnerId,
}) => {
    const odds = team.odds?.find((odd) => odd.providerId === partnerId);

    const showOddsSign = [CountryCode.Canada, CountryCode.UnitedStates].includes(
        countryCode.toUpperCase() as CountryCode
    );

    const formatOdds = (odds?: TeamOdds): ReactNode =>
        odds &&
        Number(odds.value) !== 0 && (
            <FormattedMessage
                description="Display for the game odds for a specific team"
                defaultMessage="{odd}"
                values={{
                    odd: (
                        <FormattedNumber
                            value={parseFloat(odds?.value)}
                            signDisplay={showOddsSign ? 'always' : 'negative'}
                        />
                    ),
                }}
            />
        );

    return (
        <TeamContainer className="team-container">
            <TeamLogo>
                {team.logo && team.abbrev && (
                    <TeamDisplay
                        logoUrlOverride={team.logo}
                        team={{ triCode: team.abbrev }}
                        displayName={false}
                        altTextOverride={TEAM_ALT_TEXT_OVERRIDE}
                    />
                )}
            </TeamLogo>
            {/* If there is no team record and we would show it otherwise, we want to center justify
                this div so that the team name isn't misaligned with their logo */}
            <TeamInfoContainer $centerJustify={preGame && !team.record}>
                <TeamName className="team-name">{parseValueFromLocalizedAttribute(team.name)}</TeamName>
                <TeamGameDetails>
                    {getAdditionalTeamInfo(
                        preGame,
                        team.sog,
                        team.record,
                        game.situation?.[isHomeTeam ? 'homeTeam' : 'awayTeam'].situationDescriptions,
                        game.situation?.timeRemaining
                    )}
                </TeamGameDetails>
            </TeamInfoContainer>
            <TeamOddsContainer $isHidden={!preGame || hideOdds}>{formatOdds(odds)}</TeamOddsContainer>
            <TeamScore className="team-score" $isHidden={preGame}>
                {team.score}
            </TeamScore>
        </TeamContainer>
    );
};

export default FormattedTeamDisplay;
